import { playgroundEditGroup } from '@/lib/playground-theme';
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import { OFFICIAL_PRESETS } from '@/data/official-presets';
import { PRESETS } from '@/data/presets';
import { useAccentTheme } from '@/hooks/use-accent-theme';
import { useDocumentHistory } from '@/hooks/use-document-history';
import { getNextAccentPalette } from '@/lib/accent-palettes';
import { copyTextToClipboard } from '@/lib/clipboard';
import { parseCustomPresetText } from '@/lib/custom-presets';
import { notify, notifyAfterToastReady } from '@/lib/studio-notify';
import {
  ensureAvailablePreset,
  readStoredCustomPresetsWithStatus,
  readStoredSettingsWithStatus,
  writeStoredCustomPresets,
  writeStoredSettings,
} from '@/lib/storage';
import { readStoredStudioStateWithStatus, writeStoredStudioState } from '@/lib/studio-storage';
import { createStopId, createStopInLargestGap, sortGradientStops } from '@/lib/gradient';
import {
  createColorScale,
  makeSavedPalette,
  MAX_SAVED_PALETTES,
  paletteFromColors,
  paletteShareHash,
  parsePaletteHash,
  randomizePalette,
} from '@/lib/palette-workflow';
import type { GeneratorSettings, PresetRegistry, ThemeMode } from '@/types/palette';
import type { StudioDocument } from '@/lib/project-file';
import type { PlaygroundState, SavedPalette, StudioState, StudioToolId } from '@/types/studio';

export type SaveStatus = 'saving' | 'saved' | 'error';
const resolve = <T>(value: SetStateAction<T>, previous: T): T =>
  typeof value === 'function' ? (value as (previous: T) => T)(previous) : value;
const mergePresetRegistries = (custom: PresetRegistry): PresetRegistry => ({
  ...PRESETS,
  ...OFFICIAL_PRESETS,
  ...custom,
});
const persistDocument = (document: StudioDocument): boolean =>
  [
    writeStoredStudioState(document.studio),
    writeStoredSettings(document.settings),
    writeStoredCustomPresets(document.customPresets),
  ].every(Boolean);

const applyTheme = (theme: ThemeMode) => {
  const root = document.documentElement;
  root.classList.add('theme-changing');
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
  root.dataset.theme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#141517' : '#F4F5F3');
  const frame = requestAnimationFrame(() => root.classList.remove('theme-changing'));
  return () => {
    cancelAnimationFrame(frame);
    root.classList.remove('theme-changing');
  };
};

const isEditing = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    !!target.closest('[role="dialog"], [role="listbox"], [role="menu"]'));
const SHORTCUT_TOOLS: Record<string, StudioToolId> = {
  1: 'palette',
  2: 'gradient',
  3: 'scale',
  4: 'contrast',
  5: 'playground',
  6: 'image',
  7: 'library',
};

export function useStudioSession() {
  const initialStorage = useMemo(
    () => ({
      studio: readStoredStudioStateWithStatus(),
      settings: readStoredSettingsWithStatus(),
      customPresets: readStoredCustomPresetsWithStatus(),
    }),
    [],
  );
  const initialDocument = useMemo(
    () => ({
      studio: initialStorage.studio.value,
      settings: ensureAvailablePreset(
        initialStorage.settings.value,
        mergePresetRegistries(initialStorage.customPresets.value),
      ),
      customPresets: initialStorage.customPresets.value,
    }),
    [initialStorage],
  );
  const {
    document: current,
    update,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useDocumentHistory(initialDocument);
  const { studio, settings, customPresets } = current;
  const documentRef = useRef(current);
  documentRef.current = current;
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saving');
  const corruptionReported = useRef(false);
  const storageFailureReported = useRef(false);
  const loadedHash = useRef<string | null>(null);
  const presetRegistry = useMemo(() => mergePresetRegistries(customPresets), [customPresets]);

  const flush = useCallback(() => {
    const saved = persistDocument(documentRef.current);
    setSaveStatus(saved ? 'saved' : 'error');
    if (!saved && !storageFailureReported.current)
      notifyAfterToastReady(
        'Local saving is unavailable. Your work is still open; export a copy before closing.',
        { type: 'warning' },
      );
    storageFailureReported.current = !saved;
  }, []);

  useEffect(() => {
    if (corruptionReported.current) return;
    if (
      initialStorage.studio.discarded ||
      initialStorage.settings.discarded ||
      initialStorage.customPresets.discarded
    ) {
      corruptionReported.current = true;
      notifyAfterToastReady(
        'Some saved data could not be read. Valid data was kept and missing values restored.',
        { type: 'warning' },
      );
    }
  }, [initialStorage]);

  useEffect(() => {
    setSaveStatus('saving');
    const timer = window.setTimeout(flush, 280);
    return () => window.clearTimeout(timer);
  }, [current, flush]);
  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHidden);
      persistDocument(documentRef.current);
    };
  }, [flush]);
  useEffect(() => applyTheme(settings.theme), [settings.theme]);
  useAccentTheme(settings.accentPalette);

  const setStudio = useCallback(
    (action: SetStateAction<StudioState>, options?: { group?: string | false }) => {
      const previous = documentRef.current.studio;
      const next = resolve(action, previous);
      const numericEdit =
        previous.name !== next.name
          ? 'palette-name'
          : previous.palette.colors.length === next.palette.colors.length &&
              previous.palette.colors.filter((color, i) => color !== next.palette.colors[i])
                .length === 1
            ? `palette-color-${previous.palette.colors.findIndex((color, i) => color !== next.palette.colors[i])}`
            : previous.palette.chroma !== next.palette.chroma
              ? 'palette-chroma'
              : previous.palette.lightness !== next.palette.lightness
                ? 'palette-lightness'
                : previous.gradient.angle !== next.gradient.angle
                  ? 'gradient-angle'
                  : previous.mixer.amount !== next.mixer.amount
                    ? 'mix-amount'
                    : previous.playground.radius !== next.playground.radius
                      ? 'playground-radius'
                      : next.gradient.stops.some(
                            (stop, i) => stop.position !== previous.gradient.stops[i]?.position,
                          ) && next.gradient.stops.length === previous.gradient.stops.length
                        ? 'gradient-position'
                        : playgroundEditGroup(previous.playground, next.playground);
      update((doc) => ({ ...doc, studio: resolve(action, doc.studio) }), {
        group: options?.group === false ? undefined : (options?.group ?? numericEdit),
      });
    },
    [update],
  );
  const setPlayground = useCallback(
    (playground: PlaygroundState, discrete = false) => {
      const previous = documentRef.current.studio.playground;
      const contentKeys = [
        'themes',
        'radius',
        'font',
        'fontScale',
        'density',
        'snapshots',
        'source',
      ] as const;
      if (contentKeys.every((key) => previous[key] === playground[key])) {
        // View choices follow the user across undo without replacing historical designs.
        update(
          (doc) => {
            const current = doc.studio.playground;
            const referenceId = current.snapshots.some((item) => item.id === playground.referenceId)
              ? playground.referenceId
              : null;
            return {
              ...doc,
              studio: {
                ...doc.studio,
                playground: {
                  ...current,
                  mode: playground.mode,
                  template: playground.template,
                  vision: playground.vision,
                  viewport: playground.viewport,
                  referenceId,
                  compare:
                    playground.compare === 'snapshot' && !referenceId ? 'none' : playground.compare,
                },
              },
            };
          },
          { record: false },
        );
        return;
      }
      setStudio((studio) => ({ ...studio, playground }), { group: discrete ? false : undefined });
    },
    [setStudio, update],
  );
  const setSettings = useCallback(
    (action: SetStateAction<GeneratorSettings>) => {
      update((doc) => ({ ...doc, settings: resolve(action, doc.settings) }), {
        group: 'scale-controls',
      });
    },
    [update],
  );
  const setActiveTool = useCallback(
    (activeTool: StudioToolId) =>
      update((doc) => ({ ...doc, studio: { ...doc.studio, activeTool } }), { record: false }),
    [update],
  );

  const handleCopy = useCallback(async (text: string, label: string) => {
    const copied = await copyTextToClipboard(text);
    notify(
      copied
        ? `${label} copied.`
        : 'Clipboard is unavailable. Select the visible value and copy it manually.',
      { type: copied ? 'success' : 'error' },
    );
  }, []);
  const handleThemeToggle = useCallback(() => {
    const theme = documentRef.current.settings.theme === 'dark' ? 'light' : 'dark';
    update((doc) => ({ ...doc, settings: { ...doc.settings, theme } }), { record: false });
  }, [update]);
  const handleCycleAccent = useCallback(() => {
    const accentPalette = getNextAccentPalette(documentRef.current.settings.accentPalette).id;
    update((doc) => ({ ...doc, settings: { ...doc.settings, accentPalette } }), { record: false });
  }, [update]);
  const handleTestInContrast = useCallback(
    (color: string) =>
      setStudio((previous) => ({
        ...previous,
        activeTool: 'contrast',
        contrast: { ...previous.contrast, foreground: color },
      })),
    [setStudio],
  );
  const handleAddToGradient = useCallback(
    (color: string) => {
      setStudio((previous) => {
        const stops = sortGradientStops(previous.gradient.stops);
        const id = stops.length < 8 ? createStopId() : previous.gradient.selectedStopId;
        return {
          ...previous,
          activeTool: 'gradient',
          gradient: {
            ...previous.gradient,
            selectedStopId: id,
            stops:
              stops.length < 8
                ? [...stops, { ...createStopInLargestGap(stops, id), color }]
                : stops.map((stop) => (stop.id === id ? { ...stop, color } : stop)),
          },
        };
      });
    },
    [setStudio],
  );
  const handleUsePalette = useCallback(
    (colors: string[], name: string) => {
      setStudio((previous) => ({
        ...previous,
        name,
        activeTool: 'palette',
        palette: paletteFromColors(previous.palette, colors),
      }));
    },
    [setStudio],
  );
  const handlePlayground = useCallback(() => setActiveTool('playground'), [setActiveTool]);
  const handlePaletteGradient = useCallback(() => {
    setStudio((previous) => {
      const colors = previous.palette.colors.slice(0, 8);
      const stops = colors.map((color, index) => ({
        id: `palette-stop-${index}`,
        color,
        position: Math.round((index * 100) / (colors.length - 1)),
      }));
      return {
        ...previous,
        activeTool: 'gradient',
        gradient: { ...previous.gradient, stops, selectedStopId: stops[0]!.id },
      };
    });
    if (documentRef.current.studio.palette.count > 8)
      notify('The first eight palette colors are now gradient stops.');
  }, [setStudio]);
  const handlePaletteScale = useCallback(() => {
    update((doc) => {
      const id = 'working-palette';
      const custom = {
        ...doc.customPresets,
        [id]: {
          name: doc.studio.name,
          description: 'Scales built from the working palette.',
          families: doc.studio.palette.colors.map((color, i) => ({
            id: `palette-${i + 1}`,
            name: `Color ${i + 1}`,
            steps: createColorScale(color),
          })),
        },
      };
      return {
        ...doc,
        customPresets: custom,
        studio: { ...doc.studio, activeTool: 'scale' },
        settings: {
          ...doc.settings,
          preset: id,
          hueShift: 0,
          chromaScale: 1,
          lightnessScale: 1,
          overrides: {},
        },
      };
    });
  }, [update]);
  const handleSavePalette = useCallback(() => {
    const currentStudio = documentRef.current.studio;
    if (currentStudio.library.length >= MAX_SAVED_PALETTES) {
      notify('Your library has 200 palettes. Export it and remove a palette to make room.', {
        type: 'warning',
      });
      return;
    }
    const saved = makeSavedPalette(currentStudio.name, currentStudio.palette.colors);
    setStudio((previous) => ({ ...previous, library: [saved, ...previous.library] }));
    notify(`Saved “${saved.name}” to your library.`);
  }, [setStudio]);
  const handleUpdateSaved = useCallback(
    (id: string, patch: Partial<Pick<SavedPalette, 'name' | 'favorite'>>) => {
      setStudio((previous) => ({
        ...previous,
        library: previous.library.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      }));
    },
    [setStudio],
  );
  const handleRemoveSaved = useCallback(
    (id: string) => {
      const saved = documentRef.current.studio.library.find((item) => item.id === id);
      if (!saved) return;
      setStudio((previous) => ({
        ...previous,
        library: previous.library.filter((item) => item.id !== id),
      }));
      notify(`Removed “${saved.name}”.`, {
        undo: () =>
          setStudio((previous) => ({
            ...previous,
            library: previous.library.some((item) => item.id === saved.id)
              ? previous.library
              : [saved, ...previous.library],
          })),
      });
    },
    [setStudio],
  );
  const handleImportPalettes = useCallback(
    (palettes: Array<{ name: string; colors: string[]; favorite?: boolean }>) => {
      if (documentRef.current.studio.library.length + palettes.length > MAX_SAVED_PALETTES)
        throw new Error(
          'This import would exceed the 200-palette limit. Export your library and make room first.',
        );
      const saved = palettes.map((palette) => ({
        ...makeSavedPalette(palette.name, palette.colors),
        favorite: palette.favorite === true,
      }));
      setStudio((previous) => ({ ...previous, library: [...saved, ...previous.library] }));
      notify(`Imported ${saved.length} ${saved.length === 1 ? 'palette' : 'palettes'}.`);
    },
    [setStudio],
  );
  const handleShare = useCallback(() => {
    const { palette, name } = documentRef.current.studio;
    void handleCopy(
      `${window.location.origin}${window.location.pathname}${paletteShareHash(palette.colors, name)}`,
      'Palette link',
    );
  }, [handleCopy]);

  useEffect(() => {
    const load = () => {
      const hash = window.location.hash;
      if (hash === loadedHash.current) return;
      loadedHash.current = hash;
      const shared = parsePaletteHash(hash);
      if (shared) {
        handleUsePalette(shared.colors, shared.name);
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}`,
        );
        loadedHash.current = '';
        notifyAfterToastReady('Shared palette opened. Your library is unchanged.');
      } else if (hash.includes('palette='))
        notifyAfterToastReady('This palette link is invalid. Your current work was kept.', {
          type: 'warning',
        });
    };
    load();
    window.addEventListener('hashchange', load);
    return () => window.removeEventListener('hashchange', load);
  }, [handleUsePalette]);

  useEffect(() => {
    const shortcut = (event: KeyboardEvent) => {
      if (isEditing(event.target)) return;
      if ((event.ctrlKey || event.metaKey) && !event.altKey) {
        if (event.key.toLowerCase() === 'z') {
          event.preventDefault();
          if (event.shiftKey) redo();
          else undo();
        } else if (event.key.toLowerCase() === 'y') {
          event.preventDefault();
          redo();
        } else if (event.key.toLowerCase() === 's') {
          event.preventDefault();
          handleSavePalette();
        }
        return;
      }
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const tool = SHORTCUT_TOOLS[event.key];
      if (tool) {
        event.preventDefault();
        setActiveTool(tool);
      }
      if (
        event.code === 'Space' &&
        documentRef.current.studio.activeTool === 'palette' &&
        !(event.target instanceof HTMLElement && event.target.closest('button, a, [role="button"]'))
      ) {
        event.preventDefault();
        setStudio((previous) => ({ ...previous, palette: randomizePalette(previous.palette) }));
      }
    };
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [undo, redo, handleSavePalette, setActiveTool, setStudio]);

  const handleImportPreset = async (file: File) => {
    try {
      if (file.size > 250_000) throw new Error('Choose a preset file smaller than 250 KB.');
      const imported = parseCustomPresetText(await file.text(), [
        ...Object.keys(PRESETS),
        ...Object.keys(OFFICIAL_PRESETS),
      ]);
      const ids = Object.keys(imported);
      update((doc) => ({
        ...doc,
        customPresets: { ...doc.customPresets, ...imported },
        studio: { ...doc.studio, activeTool: 'scale' },
        settings: { ...doc.settings, preset: ids[0]!, overrides: {} },
      }));
      notify(`Imported ${ids.length} custom ${ids.length === 1 ? 'preset' : 'presets'}.`);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Unable to import custom preset JSON.', {
        type: 'error',
      });
    }
  };
  const handleRemovePreset = (presetId: string) => {
    if (!customPresets[presetId]) return;
    update((doc) => {
      const next = { ...doc.customPresets };
      delete next[presetId];
      return {
        ...doc,
        customPresets: next,
        settings: ensureAvailablePreset(doc.settings, mergePresetRegistries(next)),
      };
    });
    notify(`Removed custom preset “${presetId}”.`);
  };

  const handleRestoreProject = useCallback(
    (restored: StudioDocument) => {
      update(() => restored);
      notify('Workspace restored. Undo is available if you want to return to your previous work.');
    },
    [update],
  );

  return {
    currentDocument: current,
    handleRestoreProject,
    studio,
    setStudio,
    setPlayground,
    settings,
    setSettings,
    customPresets,
    presetRegistry,
    notify,
    setActiveTool,
    handleThemeToggle,
    handleCycleAccent,
    handleCopy,
    handleTestInContrast,
    handleAddToGradient,
    handleImportPreset,
    handleRemovePreset,
    undo,
    redo,
    canUndo,
    canRedo,
    saveStatus,
    retrySave: flush,
    handleUsePalette,
    handlePlayground,
    handlePaletteGradient,
    handlePaletteScale,
    handleSavePalette,
    handleUpdateSaved,
    handleRemoveSaved,
    handleImportPalettes,
    handleShare,
  };
}
